import classNames from "classnames";
import React, { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Filters,
  Position,
  Query,
  QueryPanelLogic,
  RANGE_FILTERS,
} from "types";
import {
  getFilterIcon,
  getOperatorTitle,
  isSignOperator,
} from "helpers/utility/queryFilterHelpers";
import { useMislabelThresholdRange } from "hooks/useFeatureFlagHooks";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { getFalseRangeDisplayValue } from "helpers/utility/utilities";

import AddFilter from "views/components/QueryPanel/AddFilter";
import QueryTab from "views/components/QueryTab";

import useVqlParams from "hooks/useVqlParams";
import { useVisualSimilarityImages } from "hooks/useVisualSimilarityImages";
import styles from "./style.module.scss";
import { isCustomMetadata } from "helpers/utility/customMetadataHelpers";
import { formatDateWithHours } from "helpers/utility/formatters";
import { TrashIcon } from "./Icons";
import { AdvanceIcon } from "views/pages/DataPage/CardsView/DataPageFilters/SearchContainer/Icons";
import { Button, Dropdown, Tooltip } from "antd";
import { useDroppableRow } from "./dragAndDrop";
import { DraggableQueryTab } from "views/components/DraggableQueryTab";
import { SaveViewButton } from "../../pages/DataPage/CardsView/DataPageFilters/SaveViewButton/SaveViewButton";
import {
  LABEL_OUTLIER_QUERY_MIN_VALUE,
  RANGE_QUERY_MIN_VALUE,
} from "../../../helpers/constants/queries";

interface QueryPanelRowProps {
  type?: QueryPanelLogic;
  queries: Query[];
  onReset: () => void;
  onRemove: (q: Query) => void;
  onClick: (q: Query, p: Position) => void;
  interactive?: boolean;
  onAdvancedClick?: () => void;
  onShowOrRow?: () => void;
  hideAddFilter?: boolean;
  dropZoneId?: string;
  allowDragging?: boolean;
  isValidDropTarget?: boolean;
}

export const QueryPanelRow = ({
  queries,
  type = QueryPanelLogic.AND,
  onReset,
  onClick,
  onRemove,
  interactive = true,
  onAdvancedClick,
  onShowOrRow,
  hideAddFilter = false,
  dropZoneId = "default-drop-zone",
  allowDragging = true,
  isValidDropTarget = false,
}: QueryPanelRowProps) => {
  const mislabelsRange = useMislabelThresholdRange();

  const rowRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { isOver, combinedRef } = useDroppableRow(dropZoneId);

  // Combine the droppable ref with the rowRef
  const finalRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Set both refs
      (rowRef as any).current = node;
      combinedRef(node);
    },
    [combinedRef],
  );

  const logicMap: Record<QueryPanelLogic, string> = {
    [QueryPanelLogic.EXCLUDE]: "Exclude",
    [QueryPanelLogic.OR]: "OR",
    [QueryPanelLogic.AND]: "",
    [QueryPanelLogic.VECTOR_SEARCH]: "Vector search",
  };

  const logicText = logicMap[type] || "";

  const getTabLabels = (query: Query): string[] => {
    const { type, values } = query;

    const showFalseValue = [
      Filters.ISSUES,
      Filters.OUTLIERS,
      Filters.LABEL_OUTLIER,
    ].includes(type as Filters);

    if (showFalseValue && values.length === 2) {
      return values.map((v) =>
        getFalseRangeDisplayValue(
          parseFloat(v),
          type === Filters.LABEL_OUTLIER
            ? LABEL_OUTLIER_QUERY_MIN_VALUE
            : RANGE_QUERY_MIN_VALUE,
        ).toString(),
      );
    }

    // Handle datetime formatting for both CREATED_AT and custom metadata datetime fields
    const isDatetimeFilter =
      query.type === Filters.CREATED_AT ||
      (isCustomMetadata(query.type) && query.type.field_type === "datetime");

    if (isDatetimeFilter) {
      if (values.length === 1) {
        return [formatDateWithHours(values[0])];
      } else if (values.length === 2) {
        return [formatDateWithHours(values[0]), formatDateWithHours(values[1])];
      }
    }

    if (isCustomMetadata(query.type)) {
      const { field_type } = query.type;
      if (field_type === "link") {
        return values.map((v) => v.split("/").pop() || v);
      }
    }

    return values;
  };

  const checkIsClickAllowed = (query: Query): boolean => {
    // If thresholds do not exist for mislabels, do not open modal
    if (query.type === Filters.MISLABELS && !mislabelsRange) {
      return false;
    }

    // Default behavior
    return true;
  };

  return (
    <div
      className={classNames(styles.row, {
        [styles.dragOver]: isOver && allowDragging,
        [styles.validDropTarget]: isValidDropTarget,
      })}
      ref={finalRef}
      data-testid="query-panel-row"
    >
      {logicText && (
        <div className={styles.logic} data-testid="query-panel-row-logic-text">
          {logicText}
        </div>
      )}

      {queries.map((query) => {
        const isRange =
          !isCustomMetadata(query.type) && RANGE_FILTERS.includes(query.type);
        const isClickAllowed = interactive && checkIsClickAllowed(query);
        const labels = getTabLabels(query);
        const icon = getFilterIcon(query.type);
        const operator = getOperatorTitle(query.operator, query.type);
        const noColon = isSignOperator(operator);

        return allowDragging ? (
          <DraggableQueryTab key={query.id} query={query}>
            <QueryTab
              icon={icon}
              id={query.id}
              labels={labels}
              isRange={isRange}
              noColon={noColon}
              type={query.type}
              operator={operator}
              subType={query.subType}
              onClick={
                isClickAllowed
                  ? (position: Position) => onClick(query, position)
                  : undefined
              }
              onRemove={() => onRemove(query)}
              image={query.image}
              interactive={interactive}
            />
          </DraggableQueryTab>
        ) : (
          <QueryTab
            key={query.id}
            icon={icon}
            id={query.id}
            labels={labels}
            isRange={isRange}
            noColon={noColon}
            type={query.type}
            operator={operator}
            subType={query.subType}
            onClick={
              isClickAllowed
                ? (position: Position) => onClick(query, position)
                : undefined
            }
            onRemove={() => onRemove(query)}
            image={query.image}
            interactive={interactive}
          />
        );
      })}
      {interactive && (
        <div
          className={styles.actions}
          data-testid="query-panel-row-actions"
          ref={actionsRef}
        >
          {!hideAddFilter && (
            <>
              <AddFilter rowRef={rowRef} actionsRef={actionsRef} />
            </>
          )}
          <div className={styles.separator} />
          <div className={styles.saveViewIcon}>
            <SaveViewButton
              iconOnly
              tooltipText="Save as new view"
              fillEnabled={false}
              onQueryPanel={true}
            />
          </div>

          {queries.length > 0 && (
            <>
              <div
                className={styles.separator}
                data-testid="query-panel-row-reset-separator"
              ></div>
              <Tooltip title="Reset">
                <div
                  data-testid="query-panel-row-reset-btn"
                  className={styles.resetIcon}
                  onClick={onReset}
                >
                  <TrashIcon />
                </div>
              </Tooltip>
            </>
          )}
          {(onAdvancedClick || onShowOrRow) && (
            <>
              <div
                className={styles.separator}
                data-testid="query-panel-row-advanced-separator"
              ></div>
              <Dropdown
                menu={{
                  items: [
                    ...(onAdvancedClick
                      ? [
                          {
                            key: "exclude",
                            label: "Exclude",
                            onClick: onAdvancedClick,
                          },
                        ]
                      : []),
                    ...(onShowOrRow
                      ? [
                          {
                            key: "or",
                            label: "OR",
                            onClick: onShowOrRow,
                          },
                        ]
                      : []),
                  ],
                }}
                placement="bottomRight"
                data-testid="advanced-dropdown"
              >
                <Button
                  type={"link"}
                  className={styles.advancedBtn}
                  icon={<AdvanceIcon />}
                  data-testid="advanced-button"
                >
                  Advanced
                </Button>
              </Dropdown>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const QueryPanel = ({ datasetId }: { datasetId: string }) => {
  const dispatch = useDispatch();
  const { vqlQueries, removeVqlQuery, setVqlQueries, vqlToQuery } =
    useVqlParams();

  const { imageDataMap } = useVisualSimilarityImages({ vqlQueries, datasetId });

  const handleRemoval = (query: Query) => {
    removeVqlQuery(query.id);
  };

  const handleRemoveAll = () => {
    setVqlQueries([]);
  };

  const handleSelection = (query: Query, position: Position) => {
    dispatch(
      setSelectedQueryModal({
        id: query.id,
        type: query.type,
        position: position,
        image: query.image,
      }),
    );
  };

  const queries: Query[] = useMemo(() => {
    return vqlQueries
      .map((vqlQuery) => vqlToQuery(vqlQuery, imageDataMap))
      .filter((query): query is Query => query !== null);
  }, [vqlQueries, vqlToQuery, imageDataMap]);

  const renderRows = () => {
    return (
      <div>
        <QueryPanelRow
          key="andRow"
          queries={queries}
          onRemove={handleRemoval}
          onClick={handleSelection}
          onReset={handleRemoveAll}
          dropZoneId="include-row"
          allowDragging={false}
        />
      </div>
    );
  };

  return (
    <div className={styles.row}>
      <div className={classNames(styles.queryPanel)} data-testid="query-panel">
        {renderRows()}
      </div>
    </div>
  );
};

export default QueryPanel;
